/**
 * Client-veilige taximeter-logica op basis van kilometers (Google Maps).
 *
 * Regels:
 * - Minimaal € 50 voor elke rit van plaats naar plaats (tot en met 10 km).
 * - Daarna per (maximaal) 5 km extra € 5 erbij.
 * - Bij ritten naar of van België/Duitsland is dat € 10 per 5 km extra.
 */

export const BASE_PRICE = 50;
export const BASE_KM = 10;
export const STEP_KM = 5;
export const STEP_PRICE = 5;
/** Buitenlandritten (BE/DE): dubbel staptarief per 5 km. */
export const STEP_PRICE_ABROAD = 10;

/**
 * Prijs van een rit. Kilometers in Nederland kosten € 5 per (max) 5 km,
 * kilometers in België of Duitsland € 10 per (max) 5 km. De eerste 10 km
 * zitten in het basisbedrag en gaan altijd van de Nederlandse kilometers af.
 */
export function priceForKmSplit(
  distanceKm: number | null | undefined,
  abroadKm: number | null | undefined,
): number {
  const km = Math.max(0, Number(distanceKm ?? 0));
  const abroad = Math.min(km, Math.max(0, Number(abroadKm ?? 0)));
  const nl = km - abroad;
  const nlSteps = Math.ceil(Math.max(0, nl - BASE_KM) / STEP_KM);
  const abroadSteps = Math.ceil(abroad / STEP_KM);
  return BASE_PRICE + nlSteps * STEP_PRICE + abroadSteps * STEP_PRICE_ABROAD;
}

export function priceForKm(
  distanceKm: number | null | undefined,
  abroad: boolean | number = false,
): number {
  const km = Math.max(0, Number(distanceKm ?? 0));
  if (typeof abroad === "number") return priceForKmSplit(km, abroad);
  return priceForKmSplit(km, abroad ? km : 0);
}

/** Referentietarief van een gewone taximeter: € 4 per km + € 0,50 per minuut. */
export const REF_KM_RATE = 4;
export const REF_MIN_RATE = 0.5;

/** Wat een rit bij een gewone taximeter zou kosten. */
export function referenceMeterPrice(
  distanceKm: number | null | undefined,
  durationMin: number | null | undefined,
): number {
  return (
    Math.round((Number(distanceKm ?? 0) * REF_KM_RATE + Number(durationMin ?? 0) * REF_MIN_RATE) * 100) /
    100
  );
}

/** Voordeel van onze vaste prijs t.o.v. een gewone taximeter. */
export function savingVsMeter(
  distanceKm: number | null | undefined,
  durationMin: number | null | undefined,
): number {
  return Math.round((referenceMeterPrice(distanceKm, durationMin) - priceForKm(distanceKm)) * 100) / 100;
}

/** Vaste commissie per rit: de chauffeur krijgt de klantprijs min dit bedrag. */
export const COMMISSION = 30;

/** Chauffeursvergoeding op basis van de klantprijs (klantprijs − vaste commissie). */
export function driverPriceFor(customerPrice: number | null | undefined): number {
  return Math.round(Math.max(Number(customerPrice ?? 0) - COMMISSION, 0) * 100) / 100;
}

export function driverPriceForKm(distanceKm: number | null | undefined): number {
  return driverPriceFor(priceForKm(distanceKm));
}


export const PRICING_EXPLAINER = `Vanaf € ${BASE_PRICE} tot ${BASE_KM} km, daarna € ${STEP_PRICE} per ${STEP_KM} km extra.`;

/** Prijs per kilometer voor het traject langs de extra adressen. */
export const STOP_KM_PRICE = 1.4;

/** Minimumbedrag dat elk extra adres in dezelfde stad kost. */
export const STOP_MIN_PRICE = 10;

/** Minimumbedrag voor een extra adres in een andere stad. */
export const STOP_CITY_MIN_PRICE = 25;

/**
 * Zoveel omrijkilometers zitten bij een extra adres in een andere stad in het
 * minimum van € 25; daarboven geldt opnieuw € 1,40 per km.
 */
export const STOP_CITY_INCLUDED_KM = 10;

/**
 * Prijs met tussenstops: de basis is altijd de duurste (verste) losse rit
 * vanaf het ophaaladres. Daarnaast worden alleen de echte omrijkilometers
 * doorberekend tegen € 1,40 per km, met een minimum van € 10 per extra adres.
 * Een extra adres in een andere stad kost minimaal € 25; daarin zitten de
 * eerste 10 omrijkilometers, daarboven geldt opnieuw € 1,40 per km.
 */
export function priceWithStops(
  farthestLegKm: number | null | undefined,
  totalRouteKm: number | null | undefined,
  stopCount = 1,
  abroad: boolean | number = false,
  differentCityStops = 0,
): { base: number; extraKm: number; extraPrice: number; total: number } {
  const base = priceForKm(farthestLegKm, abroad);
  const total = Number(totalRouteKm ?? 0);
  // Omrijkilometers: de route met stops minus de verste losse rit.
  const detour = Math.max(0, total - Number(farthestLegKm ?? 0));
  const extraKm = Math.round(detour * 10) / 10;
  const stops = Math.max(1, stopCount);
  const cityStops = Math.min(stops, Math.max(0, differentCityStops));
  const localStops = stops - cityStops;
  const minPrice = localStops * STOP_MIN_PRICE + cityStops * STOP_CITY_MIN_PRICE;
  const kmPrice =
    cityStops > 0
      ? cityStops * STOP_CITY_MIN_PRICE +
        Math.max(0, extraKm - cityStops * STOP_CITY_INCLUDED_KM) * STOP_KM_PRICE +
        (localStops > 0 ? localStops * STOP_MIN_PRICE : 0)
      : extraKm * STOP_KM_PRICE;
  const extraPrice = Math.round(Math.max(kmPrice, minPrice) * 100) / 100;
  return { base, extraKm, extraPrice, total: Math.round((base + extraPrice) * 100) / 100 };
}

/**
 * Telt hoeveel tussenstops in een andere stad liggen dan het ophaaladres én
 * de eindbestemming. Stadsnamen worden genormaliseerd (kleine letters, zonder
 * "gemeente"). Stops zonder bekende plaatsnaam tellen als eigen stad.
 */
export function countDifferentCityStops(
  stopLocalities: Array<string | null | undefined>,
  ...referenceLocalities: Array<string | null | undefined>
): number {
  const norm = (value: string | null | undefined) =>
    (value ?? "")
      .toLowerCase()
      .replace(/^gemeente\s+/, "")
      .trim();
  const refs = new Set(referenceLocalities.map(norm).filter(Boolean));
  return stopLocalities.filter((locality) => {
    const city = norm(locality);
    return !city || !refs.has(city);
  }).length;
}


/**
 * Bustarief: 1 t/m 4 personen rijden op het gewone tarief. Vanaf 5 personen
 * (5–8) is een busje nodig: + € 30 voor ritten tot 30 km, daarboven + € 50.
 */
export const BUS_MIN_PASSENGERS = 5;
export const BUS_KM_THRESHOLD = 30;
export const BUS_SURCHARGE_SHORT = 30;
export const BUS_SURCHARGE_LONG = 50;

export function busSurcharge(
  distanceKm: number | null | undefined,
  passengers: number | null | undefined,
  forceBus?: boolean,
  /** Vaste busmeerprijs voor deze route (zie fixedBusExtra); gaat voor de km-regel. */
  fixedExtra?: number | null,
): number {
  if (!forceBus && Number(passengers ?? 1) < BUS_MIN_PASSENGERS) return 0;
  if (fixedExtra != null) return fixedExtra;
  return Number(distanceKm ?? 0) > BUS_KM_THRESHOLD ? BUS_SURCHARGE_LONG : BUS_SURCHARGE_SHORT;
}

/** Vanaf 4 grote koffers of meer dan 5 kleine stuks bagage is een busje nodig. */
export const BUS_MIN_LUGGAGE = 4;
export const BUS_MIN_HAND_LUGGAGE = 6;

export function needsBusForLuggage(
  luggage: number | null | undefined,
  handLuggage: number | null | undefined,
): boolean {
  return (
    Number(luggage ?? 0) >= BUS_MIN_LUGGAGE || Number(handLuggage ?? 0) >= BUS_MIN_HAND_LUGGAGE
  );
}

export type VehicleClass = "sedan" | "bus";

/** Voorstel voor het voertuig op basis van personen en bagage. */
export function suggestVehicle(
  passengers: number | null | undefined,
  luggage?: number | null,
  handLuggage?: number | null,
): VehicleClass {
  if (Number(passengers ?? 1) >= BUS_MIN_PASSENGERS) return "bus";
  return needsBusForLuggage(luggage, handLuggage) ? "bus" : "sedan";
}

/** Voertuigkeuze verplicht bus? (personen of bagage laten geen sedan toe) */
export function busRequired(
  passengers: number | null | undefined,
  luggage?: number | null,
  handLuggage?: number | null,
): boolean {
  return suggestVehicle(passengers, luggage, handLuggage) === "bus";
}


/**
 * Buitenlandtoeslag: ritten met een adres buiten Nederland (België/Duitsland)
 * kosten € 20 extra.
 */
export const ABROAD_SURCHARGE = 20;

/** Ligt een van de adressen buiten Nederland (België/Duitsland)? */
export function isAbroadRide(countries: Array<string | null | undefined>): boolean {
  return countries.some((c) => c && c.toUpperCase() !== "NL");
}

export function abroadSurcharge(countries: Array<string | null | undefined>): number {
  const abroad = countries.some((c) => c && c.toUpperCase() !== "NL");
  return abroad ? ABROAD_SURCHARGE : 0;
}
